import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { IconNote, IconTrash, IconEdit, IconCopy } from './utils/icons';
import {
  TicketModal,
  PrioritySelect,
  derivePriorityFromTags,
  buildTagsFromPriority,
  deriveTicketStatusFromTags,
  buildTagsFromStatus
} from './modals/TicketModal';

const PRIORITY_OPTIONS = [
  { value: 'alta',  label: 'Alta'  },
  { value: 'media', label: 'Media' },
  { value: 'baja',  label: 'Baja'  },
];

const PRIORITY_STYLES = {
  alta:  { bg: 'bg-rose-50',    text: 'text-rose-900',    ring: 'ring-rose-200',    dot: 'bg-rose-500'    },
  media: { bg: 'bg-amber-50',   text: 'text-amber-900',   ring: 'ring-amber-200',   dot: 'bg-amber-500'   },
  baja:  { bg: 'bg-emerald-50', text: 'text-emerald-900', ring: 'ring-emerald-200', dot: 'bg-emerald-500' },
};

export const PriorityBadge = ({ priority = 'media', className='' }) => {
  const s = PRIORITY_STYLES[priority] || PRIORITY_STYLES.media;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full ${s.bg} ${s.text} ring-1 ${s.ring} px-2.5 py-0.5 text-xs font-medium ${className}`}>
      <span className={`h-2 w-2 rounded-full ${s.dot}`} />
      {priority.charAt(0).toUpperCase() + priority.slice(1)}
    </span>
  );
};

const STATUS_TICKET_OPTIONS = [
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'listo',     label: 'Listo'     },
];

const STATUS_STYLES = {
  pendiente: { bg: 'bg-indigo-50',  text: 'text-indigo-900',  ring: 'ring-indigo-200'  },
  listo:     { bg: 'bg-emerald-50', text: 'text-emerald-900', ring: 'ring-emerald-200' },
};

export const StatusBadge = ({ status='pendiente', className='' }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pendiente;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full ${s.bg} ${s.text} ring-1 ${s.ring} px-2.5 py-0.5 text-xs font-medium ${className}`}>
      {status === 'listo' ? 'Listo' : 'Pendiente'}
    </span>
  );
};

const TicketContent = ({ content, onToggleChecklist }) => {
  const lines = (content || '').split('\n');
  return (
    <div className="prose prose-sm max-w-none text-slate-800">
      {lines.map((line, idx) => {
        const m = line.match(/^\s*-\s*\[( |x)\]\s*(.*)$/i);
        if (m) {
          const checked = m[1].toLowerCase() === 'x';
          const text = m[2];
          return (
            <div key={idx} className="flex items-start gap-2">
              <input type="checkbox" checked={checked} onChange={()=>onToggleChecklist?.(idx, checked)} className="mt-1" />
              <span className={`${checked?'line-through text-slate-400':''}`}>{text}</span>
            </div>
          );
        }
        return <p key={idx} className="mb-1">{line || <span className="opacity-30 select-none">—</span>}</p>
      })}
    </div>
  );
};

function TicketCard({ ticket, onEdit, onDelete, onTogglePin, onCopy, onToggleChecklist, onToggleStatus }) {
  const pr = ticket.priority || 'media';
  const s = PRIORITY_STYLES[pr] || PRIORITY_STYLES.media;
  const st = ticket.status || 'pendiente';

  return (
    <div className={`group relative rounded-2xl ${s.bg} ring-1 ${s.ring} transition p-4 shadow-sm`}>
      <div className="absolute right-2 top-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
        <button onClick={()=>onTogglePin(ticket)} className="rounded-full bg-white/90 px-2 py-1 text-xs text-slate-700 shadow hover:bg-white">
          {ticket.pinned ? 'Desfijar' : 'Fijar'}
        </button>
        <button onClick={()=>onCopy(ticket)} className="rounded-full bg-white/90 p-1 text-slate-700 shadow hover:bg-white" title="Copiar">
          <IconCopy />
        </button>
        <button onClick={()=>onEdit(ticket)} className="rounded-full bg-white/90 p-1 text-slate-700 shadow hover:bg-white" title="Editar">
          <IconEdit />
        </button>
        <button onClick={()=>onDelete(ticket)} className="rounded-full bg-white/90 p-1 text-rose-600 shadow hover:bg-white" title="Eliminar">
          <IconTrash />
        </button>
      </div>

      <div className="flex items-start justify-between gap-3">
        <div>
          {ticket.title ? (
            <h5 className="text-[15px] font-semibold text-slate-900 mb-1.5">{ticket.title}</h5>
          ) : (
            <span className="text-slate-600 mb-1.5">Sin título</span>
          )}
          <StatusBadge status={st} />
        </div>
        <PriorityBadge priority={pr} />
      </div>

      <TicketContent content={ticket.content} onToggleChecklist={(i,chk)=>onToggleChecklist(ticket,i,chk)} />

      <div className="mt-3">
        <button
          onClick={()=>onToggleStatus?.(ticket)}
          className={`rounded-lg px-3 py-1.5 text-xs font-semibold shadow-sm
                     ${st==='listo' ? 'bg-indigo-50 text-indigo-800 ring-1 ring-indigo-200 hover:bg-indigo-100'
                                     : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
          title={st==='listo' ? 'Marcar como Pendiente' : 'Marcar como Listo'}
        >
          {st === 'listo' ? 'Marcar como Pendiente' : 'Marcar como Listo'}
        </button>
      </div>
    </div>
  );
}

function QuickTicketBar({ onCreate }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [pinned, setPinned] = useState(false);
  const [priority, setPriority] = useState('media');
  const [status, setStatus] = useState('pendiente');
  const reset = () => { setTitle(''); setContent(''); setPinned(false); setPriority('media'); setStatus('pendiente'); };

  const submit = async () => {
    await onCreate({ title, content, pinned, priority, status });
    reset(); setOpen(false);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
      {!open ? (
        <button onClick={()=>setOpen(true)} className="w-full text-left text-slate-500 px-2 py-2 rounded-lg hover:bg-slate-50">
          Crear ticket…
        </button>
      ) : (
        <div className="space-y-3">
          <div className="grid gap-3 md:grid-cols-12">
            <div className="md:col-span-6">
              <input
                placeholder="Título"
                value={title}
                onChange={(e)=>setTitle(e.target.value)}
                className="w-full rounded-xl border-slate-200 focus:ring-2 focus:ring-slate-900/10"
              />
            </div>
            <div className="md:col-span-3">
              <PrioritySelect value={priority} onChange={setPriority} rounded="rounded-lg" />
            </div>
            <div className="md:col-span-3">
              <select
                value={status}
                onChange={(e)=>setStatus(e.target.value)}
                className="w-full rounded-lg border-slate-200 focus:ring-2 focus:ring-slate-900/10"
              >
                {STATUS_TICKET_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          <textarea
            rows={4}
            placeholder="Descripción del ticket… Usa '- [ ]' para checklists"
            value={content}
            onChange={(e)=>setContent(e.target.value)}
            className="w-full rounded-xl border-slate-200 focus:ring-2 focus:ring-slate-900/10"
          />

          <div className="flex items-center gap-3 justify-between">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={pinned} onChange={(e)=>setPinned(e.target.checked)} />
              Fijar
            </label>
            <div className="flex gap-2">
              <button onClick={()=>{reset(); setOpen(false);}} className="rounded-lg border border-slate-200 px-3 py-1.5 text-slate-700">Cancelar</button>
              <button onClick={submit} className="rounded-lg bg-slate-900 px-3 py-1.5 text-white">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function TicketsView() {
  const { token } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState(new Set(['alta','media','baja']));
  const [statusFilter, setStatusFilter] = useState(new Set(['pendiente','listo']));
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const controllerRef = useRef(null);

  const fetchTickets = useCallback(async (opts={}) => {
    setLoading(true); setError('');
    try {
      if (controllerRef.current) controllerRef.current.abort();
      controllerRef.current = new AbortController();

      const params = new URLSearchParams();
      if (opts.search?.trim()) params.set('search', opts.search.trim());

      const url = `/api/owner/notes?${params.toString()}`;
      const res = await fetch(url, { headers:{ 'Authorization': `Bearer ${token}` }, signal: controllerRef.current.signal });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.mensaje || 'Error al cargar tickets.');
      const normalized = (Array.isArray(data) ? data : []).map(n => ({
        ...n,
        priority: derivePriorityFromTags(n.tags),
        status: deriveTicketStatusFromTags(n.tags),
      }));
      setTickets(normalized);
    } catch (e) {
      if (e.name !== 'AbortError') setError(e.message || 'Error de conexión al cargar tickets.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(()=>{ fetchTickets({ search }); }, [fetchTickets]);
  useEffect(()=>{
    const t = setTimeout(()=> fetchTickets({ search }), 350);
    return ()=> clearTimeout(t);
  }, [search, fetchTickets]);

  const filtered = useMemo(()=>{
    let list = tickets;
    if (priorityFilter.size) {
      list = list.filter(t => priorityFilter.has(t.priority || 'media'));
    }
    if (statusFilter.size) {
      list = list.filter(t => statusFilter.has(t.status || 'pendiente'));
    }
    return [...list].sort(
      (a,b)=> (b.pinned - a.pinned) ||
              (new Date(b.updated_at||b.created_at||0) - new Date(a.updated_at||a.created_at||0))
    );
  }, [tickets, priorityFilter, statusFilter]);

  const createTicket = async ({ title, content, pinned, priority, status }) => {
    try {
      const res = await fetch(`/api/owner/notes`, {
        method:'POST',
        headers:{ 'Content-Type':'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          company: 'PERSONAL',
          title, content, pinned: !!pinned,
          tags: [...buildTagsFromPriority(priority), ...buildTagsFromStatus(status || 'pendiente')].join(',')
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.mensaje || 'No se pudo crear el ticket');
      fetchTickets({ search });
    } catch (e) { alert(e.message || 'Error creando ticket'); }
  };

  const saveTicket = async (ticket) => {
    try {
      const res = await fetch(`/api/owner/notes/${ticket.id}`, {
        method:'PUT',
        headers:{ 'Content-Type':'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          company: ticket.company || 'PERSONAL',
          title: ticket.title || '',
          content: ticket.content || '',
          pinned: !!ticket.pinned,
          tags: [...buildTagsFromPriority(ticket.priority || 'media'), ...buildTagsFromStatus(ticket.status || 'pendiente')].join(',')
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.mensaje || 'No se pudo guardar');
      fetchTickets({ search });
    } catch (e) { alert(e.message || 'Error guardando'); }
  };

  const deleteTicket = async (ticket) => {
    if (!window.confirm('¿Eliminar este ticket?')) return;
    try {
      const res = await fetch(`/api/owner/notes/${ticket.id}`, { method:'DELETE', headers:{ 'Authorization': `Bearer ${token}` } });
      if (!res.ok) {
        const data = await res.json().catch(()=> ({}));
        throw new Error(data?.mensaje || 'No se pudo eliminar');
      }
      fetchTickets({ search });
    } catch (e) { alert(e.message || 'Error eliminando'); }
  };

  const togglePin = (ticket) => saveTicket({ ...ticket, pinned: !ticket.pinned });
  const copyTicket = async (ticket) => { 
    const header = ticket.title ? `${ticket.title} ${ticket.status==='listo' ? '[Listo]' : '[Pendiente]'}\n` : '';
    const body = ticket.content || '';
    const text = header + body;
    try { await navigator.clipboard.writeText(text); } catch {} 
  };

  const toggleChecklist = (ticket, lineIdx, checked) => {
    const lines = (ticket.content || '').split('\n');
    const line = lines[lineIdx] || '';
    const replaced = line.replace(/^\s*-\s*\[( |x)\]\s*/i, `- [${checked ? ' ' : 'x'}] `);
    lines[lineIdx] = replaced;
    saveTicket({ ...ticket, content: lines.join('\n') });
  };

  const togglePriorityFilter = (p) => {
    setPriorityFilter(prev => {
      const next = new Set(prev);
      next.has(p) ? next.delete(p) : next.add(p);
      return next;
    });
  };

  const toggleStatus = (ticket) =>
    saveTicket({ ...ticket, status: (ticket.status === 'listo' ? 'pendiente' : 'listo') });

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
            <IconNote className="h-4 w-4" /> Pendientes
          </span>
          <span className="text-slate-500 text-sm">Tareas y pendientes con prioridad</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <svg viewBox="0 0 24 24" className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>
            </svg>
            <input
              value={search}
              onChange={(e)=>setSearch(e.target.value)}
              placeholder="Buscar pendientes…"
              className="w-64 rounded-xl border border-slate-200 pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-slate-900/10"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {PRIORITY_OPTIONS.map(o => {
          const active = priorityFilter.has(o.value);
          const s = PRIORITY_STYLES[o.value];
          return (
            <button
              key={o.value}
              onClick={()=>togglePriorityFilter(o.value)}
              className={`rounded-full px-3 py-1 text-xs ring-1 transition ${active ? `${s.bg} ${s.text} ring-0` : 'bg-white text-slate-700 ring-slate-200 hover:bg-slate-50'}`}
            >
              <span className={`inline-block h-2 w-2 rounded-full mr-1 align-middle ${s.dot}`} />
              {o.label}
            </button>
          );
        })}
        {STATUS_TICKET_OPTIONS.map(o => {
          const active = statusFilter.has(o.value);
          const s = STATUS_STYLES[o.value];
          return (
            <button
              key={o.value}
              onClick={()=>{
                setStatusFilter(prev=>{
                  const next = new Set(prev);
                  next.has(o.value) ? next.delete(o.value) : next.add(o.value);
                  return next;
                });
              }}
              className={`rounded-full px-3 py-1 text-xs ring-1 transition ${active ? `${s.bg} ${s.text} ring-0` : 'bg-white text-slate-700 ring-slate-200 hover:bg-slate-50'}`}
            >
              {o.label}
            </button>
          );
        })}
        {(priorityFilter.size < 3 || statusFilter.size < 2) && (
          <button onClick={()=>{ setPriorityFilter(new Set(['alta','media','baja'])); setStatusFilter(new Set(['pendiente','listo'])); }} className="rounded-full bg-white px-3 py-1 text-xs text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50">
            Ver todas
          </button>
        )}
        {(priorityFilter.size === 3 && statusFilter.size === 2) && (
          <button onClick={()=>{ setPriorityFilter(new Set()); setStatusFilter(new Set()); }} className="rounded-full bg-white px-3 py-1 text-xs text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50">
            Limpiar
          </button>
        )}
      </div>

      <QuickTicketBar onCreate={createTicket} />

      {error && <p className="text-rose-600">{error}</p>}
      {loading && <p className="text-slate-500">Cargando…</p>}

      {!loading && filtered.length === 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
          No hay pendientes. Crea uno arriba 👆
        </div>
      )}

      {filtered.length > 0 && (
        <>
          {filtered.filter(n => n.pinned).length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Fijados</h4>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filtered.filter(n => n.pinned).map(n => (
                  <TicketCard
                    key={n.id}
                    ticket={n}
                    onEdit={(t)=>{ setEditing(t); setModalOpen(true); }}
                    onDelete={deleteTicket}
                    onTogglePin={togglePin}
                    onCopy={copyTicket}
                    onToggleChecklist={toggleChecklist}
                    onToggleStatus={toggleStatus}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            {filtered.filter(n => n.pinned).length > 0 && <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Otros</h4>}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.filter(n => !n.pinned).map(n => (
                <TicketCard
                  key={n.id}
                  ticket={n}
                  onEdit={(t)=>{ setEditing(t); setModalOpen(true); }}
                  onDelete={deleteTicket}
                  onTogglePin={togglePin}
                  onCopy={copyTicket}
                  onToggleChecklist={toggleChecklist}
                  onToggleStatus={toggleStatus}
                />
              ))}
            </div>
          </div>
        </>
      )}

      <TicketModal
        open={modalOpen}
        onClose={()=>{ setModalOpen(false); setEditing(null); }}
        onSubmit={()=>fetchTickets({ search })}
        defaultValue={editing}
      />
    </div>
  );
}
